import get from 'lodash/get';
import partial from 'lodash/partial';

import { Editor, Path, Point, Transforms } from '@lumapps/wrex/slate';
import { createPlugin, CreatePluginEditor } from '@lumapps/wrex/slate/plugin';
import { getSibling } from '@lumapps/wrex/slate/utils/getSibling';
import type { WrexEditor } from '@lumapps/wrex/types';

import { EditableColumn } from '../components/editableBlocks/EditableColumn';
import { EditableColumns } from '../components/editableBlocks/EditableColumns';
import type { ColumnsEditor, ColumnsEditorOptions } from '../types';
import { insertColumns } from '../utils/insertColumns';
import { isColumn } from '../utils/isColumn';
import { isColumns } from '../utils/isColumns';
import { isColumnsAllowed } from '../utils/isColumnsAllowed';
import { getKeyHandler } from './event/keyboard';
import { normalizeColumn } from './normalize/normalizeColumn';
import { normalizeColumns } from './normalize/normalizeColumns';

const createPluginEditor =
    (options: ColumnsEditorOptions): CreatePluginEditor<ColumnsEditor, WrexEditor<ColumnsEditor>> =>
    (editor) => {
        const { deleteBackward, deleteForward, normalizeNode } = editor;
        const { allowedElements } = options;

        return {
            isColumnsAllowed: isColumnsAllowed(editor),
            deleteBackward(unit) {
                const { selection } = editor;
                if (unit === 'character' && selection) {
                    const [columnAtSelection] = Editor.nodes(editor, { match: isColumn });
                    // we don't allow deletion when cursor is in column
                    if (columnAtSelection) {
                        const isAtStart = Point.equals(selection.anchor, Editor.start(editor, columnAtSelection[1]));
                        if (isAtStart) {
                            return;
                        }
                    } else {
                        const focusedPath = selection.focus.path;
                        const [blockBeforeNode] = getSibling(editor, [focusedPath[0]], 'before') || [];
                        const isAtStart = Point.equals(selection.anchor, Editor.start(editor, focusedPath));

                        // if cursor is outside columns (just after) and at the beginning of line
                        // we manually move the cursor inside the last column. Without this logic
                        // deleteBackward from slate will run and apply the default logic meaning it
                        // will merge the current block into the previous one. For example: if cursor is
                        // at beginning of paragraph just after columns, when delete backward
                        // it will move the entire paragraph inside the last column.
                        // We avoid that with this code
                        if (blockBeforeNode && isColumns(blockBeforeNode) && isAtStart) {
                            const newTargetPoint = Editor.before(editor, selection.focus.path, {
                                distance: 1,
                                unit: 'block',
                            });

                            if (newTargetPoint) {
                                Transforms.select(editor, {
                                    anchor: newTargetPoint,
                                    focus: newTargetPoint,
                                });
                                return;
                            }
                        }
                    }
                }
                deleteBackward(unit);
            },
            deleteForward(unit) {
                const { selection } = editor;
                if (unit === 'character' && selection) {
                    const at = selection.focus;
                    const next = Editor.after(editor, at);
                    const firstPathNumber = [get(next, ['path', 0]) as number];
                    const firstColPath = [...firstPathNumber, 0, 0, 0];
                    const [columnAtSelection] = Editor.nodes(editor, { match: isColumn });

                    // when deletingForward from before columns it doesn't select the first column
                    // so, if has not been selected, we select it and set cursor at beginning first column
                    if (!columnAtSelection && next && Path.equals(next?.path, firstColPath)) {
                        Transforms.select(editor, {
                            anchor: { path: firstColPath, offset: 0 },
                            focus: { path: firstColPath, offset: 0 },
                        });
                        return;
                    }

                    // we don't allow deletion when cursor at the end of column
                    if (columnAtSelection) {
                        const isAtEnd = Point.equals(selection.anchor, Editor.end(editor, columnAtSelection[1]));
                        if (isAtEnd) {
                            return;
                        }
                    }
                }
                deleteForward(unit);
            },
            getColumnsMaxNumber: () => options.maxNumber,
            insertColumns: partial(insertColumns, editor),
            normalizeNode([node, path]) {
                const pathRef = Editor.pathRef(editor, path);

                if (normalizeColumns([node, path], editor)) {
                    return;
                }

                if (normalizeColumn([node, path], editor, allowedElements)) {
                    return;
                }

                const changedPath = pathRef.unref();
                if (changedPath) {
                    normalizeNode([node, changedPath]);
                }
            },
        };
    };

/**
 * A plugin that can handle columns.
 */
export const withColumns = (options: ColumnsEditorOptions) =>
    createPlugin<ColumnsEditor, WrexEditor<ColumnsEditor>>({
        createPluginEditor: createPluginEditor(options),
        getKeyHandler,
        elements: [EditableColumns, EditableColumn],
    });
